import { useState, useCallback, useRef, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import type { PageResponse } from "@/lib/pagination";
import { buildListQuery } from "@/lib/pagination";
import type {
  Publication,
  PublicationImportMode,
  PublicationImportPreview,
  PublicationImportResult,
} from "@/lib/types";
import Pagination from "@/components/base/Pagination";
import ScrollReveal from "@/components/base/ScrollReveal";
import { useServerPagination } from "@/lib/useServerPagination";

type FormMode = "list" | "create" | "edit";

const emptyForm = {
  pubYear: String(new Date().getFullYear()),
  title: "",
  authors: "",
  journal: "",
  citation: "",
  displayText: "",
  sortOrder: "0",
  status: "active",
};

const inputCls =
  "w-full px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-[#1977cc] focus:ring-1 focus:ring-[#1977cc]/20 bg-white";

const selectCls =
  "px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-[#1977cc] bg-white";

const IMPORT_MODES: { value: PublicationImportMode; label: string }[] = [
  { value: "upsert", label: "Upsert (add / update by id)" },
  { value: "append", label: "Append only" },
  { value: "replace_all", label: "Replace all (delete existing)" },
];

const YEAR_OPTIONS = Array.from({ length: 2026 - 1982 }, (_, i) => 2025 - i);

function buildDisplayPreview(form: typeof emptyForm) {
  if (form.displayText.trim()) return form.displayText.trim();
  const parts = [form.title, form.authors, form.journal, form.citation].filter(Boolean);
  if (parts.length === 0) return "";
  return parts.reduce((acc, part, i) => {
    if (i === 0) return part;
    if (i === 1) return `${acc}. ${part}`;
    return `${acc}, ${part}`;
  }, "");
}

export default function AdminPublicationsPage() {
  const [mode, setMode] = useState<FormMode>("list");
  const [showImportPanel, setShowImportPanel] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [editId, setEditId] = useState<number | null>(null);
  const [search, setSearch] = useState("");
  const [filterYear, setFilterYear] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [toast, setToast] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [importMode, setImportMode] = useState<PublicationImportMode>("upsert");
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importPreview, setImportPreview] = useState<PublicationImportPreview | null>(null);
  const [importLoading, setImportLoading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const showToast = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  };

  const list = useServerPagination<Publication>({
    resetDeps: [search, filterYear, filterStatus],
    enabled: mode === "list",
    fetchPage: useCallback(
      async (page, size) => {
        const res = await api.getPage<PageResponse<Publication>>(
          "/admin/publications" +
            buildListQuery(page + 1, size, {
              search: search || undefined,
              year: filterYear || undefined,
              status: filterStatus || undefined,
            })
        );
        return res.data;
      },
      [search, filterYear, filterStatus]
    ),
  });

  const previewDisplay = useMemo(() => buildDisplayPreview(form), [form]);

  const resetForm = () => {
    setForm({ ...emptyForm });
    setEditId(null);
    setMode("list");
  };

  const handleDelete = async (id: number) => {
    if (!confirm("정말 삭제하시겠습니까?")) return;
    try {
      await api.delete(`/admin/publications/${id}`);
      showToast("삭제되었습니다.");
      await list.reload();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : "삭제 실패");
    }
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      showToast("제목을 입력해주세요.");
      return;
    }
    const pubYear = parseInt(form.pubYear, 10);
    if (Number.isNaN(pubYear) || pubYear < 1900 || pubYear > 2100) {
      showToast("연도(1900~2100)를 확인해주세요.");
      return;
    }
    const payload = {
      pubYear,
      title: form.title.trim(),
      authors: form.authors.trim() || null,
      journal: form.journal.trim() || null,
      citation: form.citation.trim() || null,
      displayText: form.displayText.trim() || null,
      sortOrder: parseInt(form.sortOrder, 10) || 0,
      status: form.status,
    };
    setSaving(true);
    try {
      if (mode === "edit" && editId) {
        await api.put(`/admin/publications/${editId}`, payload);
        showToast("수정되었습니다.");
      } else {
        await api.post("/admin/publications", payload);
        showToast("생성되었습니다.");
      }
      resetForm();
      await list.reload();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : "저장 실패");
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (item: Publication) => {
    setEditId(item.id);
    setForm({
      pubYear: String(item.pubYear),
      title: item.title,
      authors: item.authors ?? "",
      journal: item.journal ?? "",
      citation: item.citation ?? "",
      displayText: item.displayText ?? "",
      sortOrder: String(item.sortOrder ?? 0),
      status: item.status,
    });
    setMode("edit");
  };

  const handleDownloadTemplate = async () => {
    try {
      await api.download("/admin/publications/import/template", "publications_template.xlsx");
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : "템플릿 다운로드 실패");
    }
  };

  const applyImportFile = (file: File | null) => {
    if (!file) return;
    if (!file.name.endsWith(".xlsx") && !file.name.endsWith(".xls")) {
      showToast("xlsx 또는 xls 파일만 업로드할 수 있습니다.");
      return;
    }
    setImportFile(file);
    setImportPreview(null);
  };

  const handlePreviewImport = async () => {
    if (!importFile) {
      showToast("엑셀 파일을 선택해주세요.");
      return;
    }
    setImportLoading(true);
    try {
      const res = await api.uploadWithParams<PublicationImportPreview>(
        "/admin/publications/import/preview",
        importFile,
        { mode: importMode }
      );
      setImportPreview(res.data);
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : "미리보기 실패");
      setImportPreview(null);
    } finally {
      setImportLoading(false);
    }
  };

  const handleExecuteImport = async () => {
    if (!importFile) {
      showToast("엑셀 파일을 선택해주세요.");
      return;
    }
    if (importMode === "replace_all") {
      const msg = importPreview
        ? `기존 ${importPreview.currentCount}건을 삭제하고 ${importPreview.totalRows}건을 등록합니다. 계속하시겠습니까?`
        : "기존 Publications 데이터가 모두 삭제됩니다. 계속하시겠습니까?";
      if (!confirm(msg)) return;
    }
    setImportLoading(true);
    try {
      const res = await api.uploadWithParams<PublicationImportResult>(
        "/admin/publications/import",
        importFile,
        { mode: importMode }
      );
      const r = res.data;
      showToast(`완료: 추가 ${r.inserted}, 수정 ${r.updated}, 삭제 ${r.deleted}`);
      setImportPreview(null);
      setImportFile(null);
      setShowImportPanel(false);
      await list.reload();
    } catch (err: unknown) {
      showToast(err instanceof Error ? err.message : "Import 실패");
    } finally {
      setImportLoading(false);
    }
  };

  return (
    <>
      {toast && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-[60] px-5 py-2.5 bg-[#2c4964] text-white text-sm rounded-full shadow-lg">
          {toast}
        </div>
      )}

      <div>
        {mode === "list" && (
          <>
            <ScrollReveal>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div className="flex flex-col sm:flex-row flex-wrap gap-3">
                  <div className="relative">
                    <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                    <input
                      type="search"
                      placeholder="Search publications..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:border-[#1977cc] focus:ring-1 focus:ring-[#1977cc]/20 w-full sm:w-64"
                    />
                  </div>
                  <select
                    value={filterYear}
                    onChange={(e) => setFilterYear(e.target.value)}
                    className={selectCls}
                  >
                    <option value="">All Years</option>
                    {YEAR_OPTIONS.map((y) => (
                      <option key={y} value={String(y)}>{y}</option>
                    ))}
                  </select>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className={selectCls}
                  >
                    <option value="">All Status</option>
                    <option value="active">active</option>
                    <option value="hidden">hidden</option>
                  </select>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <Link
                    to="/publications"
                    target="_blank"
                    className="inline-flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
                  >
                    <i className="ri-external-link-line" /> View
                  </Link>
                  <button
                    type="button"
                    onClick={() => setShowImportPanel((v) => !v)}
                    className={`inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md border transition-colors cursor-pointer whitespace-nowrap ${
                      showImportPanel
                        ? "border-[#1977cc] bg-[#f1f7fc] text-[#1977cc]"
                        : "border-gray-200 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <i className="ri-file-excel-2-line" />
                    Excel Import
                  </button>
                  <button
                    onClick={() => {
                      setMode("create");
                      setForm({ ...emptyForm });
                      setEditId(null);
                    }}
                    className="inline-flex items-center gap-2 px-4 py-2 bg-[#1977cc] text-white text-sm font-medium rounded-md hover:bg-[#1669b3] transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-add-line" /> Add Publication
                  </button>
                </div>
              </div>
            </ScrollReveal>

            {showImportPanel && (
              <ScrollReveal delay={0.03}>
                <div className="mb-6 rounded-xl border border-gray-200 bg-white p-5 md:p-6 space-y-5">
                  <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                    <div>
                      <h3 className="text-sm font-semibold text-[#2c4964]">Excel Import</h3>
                      <p className="text-xs text-gray-500 mt-1">
                        Template includes AIBI publications (542 rows). Download → upload → preview → apply.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={handleDownloadTemplate}
                      className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-200 rounded-md hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-download-2-line text-[#1977cc]" />
                      Download Template
                    </button>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-[#2c4964] mb-1.5">Import Mode</label>
                      <select
                        value={importMode}
                        onChange={(e) => {
                          setImportMode(e.target.value as PublicationImportMode);
                          setImportPreview(null);
                        }}
                        className={inputCls}
                      >
                        {IMPORT_MODES.map((m) => (
                          <option key={m.value} value={m.value}>{m.label}</option>
                        ))}
                      </select>
                      {importMode === "replace_all" && (
                        <p className="mt-1 text-xs text-amber-600">
                          <i className="ri-error-warning-line" /> Replaces all existing publications.
                        </p>
                      )}
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-[#2c4964] mb-1.5">Excel File</label>
                      <div
                        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                        onDragLeave={() => setDragOver(false)}
                        onDrop={(e) => {
                          e.preventDefault();
                          setDragOver(false);
                          applyImportFile(e.dataTransfer.files[0] ?? null);
                        }}
                        className={`flex items-center gap-3 px-3 py-2 border rounded-md cursor-pointer transition-colors ${
                          dragOver ? "border-[#1977cc] bg-[#f1f7fc]" : "border-gray-200 hover:border-gray-300"
                        }`}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept=".xlsx,.xls"
                          className="hidden"
                          onChange={(e) => applyImportFile(e.target.files?.[0] ?? null)}
                        />
                        <i className={`ri-file-excel-2-line text-lg ${importFile ? "text-emerald-600" : "text-gray-400"}`} />
                        <span className="text-sm text-gray-600 truncate flex-1">
                          {importFile ? importFile.name : "Choose or drop .xlsx file"}
                        </span>
                        {importFile && (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setImportFile(null);
                              setImportPreview(null);
                            }}
                            className="text-xs text-gray-400 hover:text-gray-600 cursor-pointer"
                          >
                            Clear
                          </button>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      disabled={importLoading || !importFile}
                      onClick={handlePreviewImport}
                      className="inline-flex items-center gap-1.5 px-4 py-2 text-sm border border-[#1977cc] text-[#1977cc] rounded-md hover:bg-[#f1f7fc] disabled:opacity-50 cursor-pointer"
                    >
                      {importLoading ? <i className="ri-loader-4-line animate-spin" /> : null}
                      Preview
                    </button>
                    <button
                      type="button"
                      disabled={importLoading || !importFile || !importPreview || importPreview.errorCount > 0}
                      onClick={handleExecuteImport}
                      className="px-4 py-2 text-sm bg-[#1977cc] text-white rounded-md hover:bg-[#1669b3] disabled:opacity-50 cursor-pointer"
                    >
                      Apply
                    </button>
                  </div>

                  {importPreview && (
                    <div className="text-sm border-t border-gray-100 pt-4 space-y-3">
                      <p className="text-gray-600">
                        Current <strong>{importPreview.currentCount}</strong> ·
                        Rows <strong>{importPreview.totalRows}</strong> ·
                        Insert <strong className="text-green-700">{importPreview.insertCount}</strong> ·
                        Update <strong>{importPreview.updateCount}</strong> ·
                        Errors <strong className={importPreview.errorCount > 0 ? "text-red-600" : ""}>{importPreview.errorCount}</strong>
                      </p>
                      {importPreview.errorCount > 0 ? (
                        <div className="max-h-36 overflow-auto rounded-md border border-red-100 bg-red-50 p-3 text-xs text-red-700 space-y-1">
                          {importPreview.rows
                            .filter((r) => r.action === "error")
                            .map((r) => (
                              <div key={r.rowNo}>Row {r.rowNo}: {r.message}</div>
                            ))}
                        </div>
                      ) : (
                        <p className="text-xs text-green-700">Validation passed. Click Apply to save.</p>
                      )}
                    </div>
                  )}
                </div>
              </ScrollReveal>
            )}

            <ScrollReveal delay={0.05}>
              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#f1f7fc]">
                      <th className="px-4 py-3 text-left text-[#2c4964] font-semibold border-b border-gray-200 w-16">ID</th>
                      <th className="px-4 py-3 text-left text-[#2c4964] font-semibold border-b border-gray-200 w-20">Year</th>
                      <th className="px-4 py-3 text-left text-[#2c4964] font-semibold border-b border-gray-200">Title</th>
                      <th className="px-4 py-3 text-left text-[#2c4964] font-semibold border-b border-gray-200 w-36">Journal</th>
                      <th className="px-4 py-3 text-left text-[#2c4964] font-semibold border-b border-gray-200 w-16">Sort</th>
                      <th className="px-4 py-3 text-left text-[#2c4964] font-semibold border-b border-gray-200 w-24">Status</th>
                      <th className="px-4 py-3 text-left text-[#2c4964] font-semibold border-b border-gray-200 w-44">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {list.loading ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-8 text-center text-gray-400">Loading...</td>
                      </tr>
                    ) : list.items.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-4 py-8 text-center text-gray-400">No publications found.</td>
                      </tr>
                    ) : (
                      list.items.map((item) => (
                        <tr key={item.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-4 py-3 border-b border-gray-100 text-gray-500">{item.id}</td>
                          <td className="px-4 py-3 border-b border-gray-100 text-gray-700 whitespace-nowrap">{item.pubYear}</td>
                          <td className="px-4 py-3 border-b border-gray-100 text-gray-700 font-medium max-w-xs truncate" title={item.title}>
                            {item.title}
                          </td>
                          <td className="px-4 py-3 border-b border-gray-100 text-gray-500 text-xs max-w-[140px] truncate" title={item.journal ?? ""}>
                            {item.journal || "—"}
                          </td>
                          <td className="px-4 py-3 border-b border-gray-100 text-gray-500">{item.sortOrder}</td>
                          <td className="px-4 py-3 border-b border-gray-100">
                            <span
                              className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${
                                item.status === "active"
                                  ? "bg-green-50 text-green-700"
                                  : "bg-gray-100 text-gray-500"
                              }`}
                            >
                              {item.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 border-b border-gray-100">
                            <div className="flex items-center gap-2">
                              <Link
                                to="/publications"
                                target="_blank"
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded transition-colors"
                              >
                                <i className="ri-eye-line" /> View
                              </Link>
                              <button
                                onClick={() => openEdit(item)}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-[#1977cc] hover:bg-[#f1f7fc] rounded transition-colors cursor-pointer"
                              >
                                <i className="ri-edit-line" /> Edit
                              </button>
                              <button
                                onClick={() => handleDelete(item.id)}
                                className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 rounded transition-colors cursor-pointer"
                              >
                                <i className="ri-delete-bin-line" /> Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
                {!list.loading && (
                  <Pagination
                    page={list.page}
                    totalPages={list.totalPages}
                    totalItems={list.totalItems}
                    pageSize={list.pageSize}
                    rangeStart={list.rangeStart}
                    rangeEnd={list.rangeEnd}
                    onPageChange={list.setPage}
                    onPageSizeChange={list.setPageSize}
                    pageSizeOptions={list.pageSizeOptions}
                  />
                )}
              </div>
            </ScrollReveal>
          </>
        )}

        {(mode === "create" || mode === "edit") && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
          >
            <div className="max-w-3xl mx-auto bg-white rounded-xl border border-gray-200 p-6 md:p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-[#2c4964]">
                  {mode === "create" ? "Add Publication" : "Edit Publication"}
                </h2>
                <button
                  onClick={resetForm}
                  className="text-sm text-gray-500 hover:text-[#1977cc] transition-colors cursor-pointer"
                >
                  Cancel
                </button>
              </div>

              <div className="space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-[#2c4964] mb-1.5">
                      Year <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="number"
                      value={form.pubYear}
                      onChange={(e) => setForm((f) => ({ ...f, pubYear: e.target.value }))}
                      className={inputCls}
                      min={1900}
                      max={2100}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#2c4964] mb-1.5">Sort Order</label>
                    <input
                      type="number"
                      value={form.sortOrder}
                      onChange={(e) => setForm((f) => ({ ...f, sortOrder: e.target.value }))}
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#2c4964] mb-1.5">Status</label>
                    <select
                      value={form.status}
                      onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                      className={inputCls}
                    >
                      <option value="active">active</option>
                      <option value="hidden">hidden</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#2c4964] mb-1.5">
                    Title <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={form.title}
                    onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                    rows={3}
                    className={`${inputCls} resize-none`}
                    placeholder="Publication title"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#2c4964] mb-1.5">Authors</label>
                  <textarea
                    value={form.authors}
                    onChange={(e) => setForm((f) => ({ ...f, authors: e.target.value }))}
                    rows={2}
                    className={`${inputCls} resize-none`}
                    placeholder="Author A, Author B, ..."
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-medium text-[#2c4964] mb-1.5">Journal</label>
                    <input
                      type="text"
                      value={form.journal}
                      onChange={(e) => setForm((f) => ({ ...f, journal: e.target.value }))}
                      className={inputCls}
                      placeholder="e.g. Nature, Sci Rep."
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-[#2c4964] mb-1.5">Citation</label>
                    <input
                      type="text"
                      value={form.citation}
                      onChange={(e) => setForm((f) => ({ ...f, citation: e.target.value }))}
                      className={inputCls}
                      placeholder="e.g. 15: 11099, 2025"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-[#2c4964] mb-1.5">Display Text</label>
                  <textarea
                    value={form.displayText}
                    onChange={(e) => setForm((f) => ({ ...f, displayText: e.target.value }))}
                    rows={3}
                    className={`${inputCls} resize-none`}
                    placeholder="Optional. Auto-generated from fields above if empty."
                  />
                  {previewDisplay && (
                    <p className="mt-2 text-xs text-gray-500 leading-relaxed">
                      Preview: {previewDisplay}
                    </p>
                  )}
                </div>

                <div className="flex items-center justify-end gap-3 pt-2">
                  <button
                    onClick={resetForm}
                    disabled={saving}
                    className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-md transition-colors cursor-pointer disabled:opacity-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-5 py-2 bg-[#1977cc] text-white text-sm font-medium rounded-md hover:bg-[#1669b3] disabled:opacity-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    {saving ? "Saving..." : mode === "create" ? "Create Publication" : "Save Changes"}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </div>
    </>
  );
}
